<template>
  <v-container>
    <v-card
      :loading="false"
    >
      <v-overlay
        :absolute="true"
        :value="isComponentLoading"
      >
        <v-progress-circular indeterminate size="24"></v-progress-circular>
      </v-overlay>
      <v-row class="pa-3">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="searchText"
            :hint="lang($store, 'sap/listings.global.filter.hints.search_name_contact')"
            persistent-hint
            single-line
            :loading="false"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="searchParams.region"
            :hint="lang($store, 'sap/listings.global.filter.hints.region')"
            :items="regionsItems"
            persistent-hint
            single-line
            :loading="false"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-menu
            v-model="menuFrom"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="fromDateFormatted"
                @input="changeDate('from', $event)"
                :label="lang($store, 'sap/listings.global.filter.hints.date_from')"
                prepend-icon="mdi-calendar"
                readonly
                clearable
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="searchParams.from" no-title
                           :first-day-of-week="1"
                           :locale="getCurrentLocale()"
                           @input="menuFrom = false"></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="6">
          <v-menu
            v-model="menuTo"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="toDateFormatted"
                @input="changeDate('to', $event)"
                :label="lang($store, 'sap/listings.global.filter.hints.date_to')"
                prepend-icon="mdi-calendar"
                readonly
                clearable
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="searchParams.to" no-title
                           :first-day-of-week="1"
                           :locale="getCurrentLocale()"
                           @input="menuTo = false"></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="searchParams.type"
            :items="estimationTypeItems"
            prepend-icon="mdi-cart-arrow-right"
            single-line
            :hint="lang($store, 'sap/listings.global.filter.hints.type')"
            persistent-hint
          ></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            multiple
            v-model="searchParams.statuses"
            :items="estimationStatusItems"
            prepend-icon="mdi-cart-arrow-right"
            single-line
            :hint="lang($store, 'sap/listings.global.filter.hints.order_status')"
            persistent-hint
          ></v-select>
        </v-col>
      </v-row>
      <download-file-button
        v-if="account.role === MANAGER_TOP"
        :use-download-report-users="true"
        style="position: absolute; top: 5px; right: 5px"
        @click="$emit('download-excel')"
      />
    </v-card>
  </v-container>
</template>

<script>
import moment from 'moment';
/* constants */
import {
  ESTIMATED,
  ESTIMATING,
  NOT_SUITABLE,
  ORDERED,
  WAITING_PHOTO,
} from '@/constants/estimation_statuses';
import { MANAGER_TOP } from '@/constants/roles';
import { DATE_ISO } from '@/constants/dates';

/* mixins */
import regionsFilter from '@/mixins/filters/regionsFilter';
import payedFilter from '@/mixins/filters/payedFilter';
import searchTextFilter from '@/mixins/filters/searchTextFilter';
import searchCleanerFilter from '@/mixins/filters/searchCleanerFilter';
import estimationStatusFilter from '@/mixins/filters/estimationStatusFilter';
import estimationTypeFilter from '@/mixins/filters/estimationTypeFilter';
import isComponentLoading from '@/mixins/isComponentLoading';
/* components */
import DownloadFileButton from '@/components/minialerts/DownloadFileButton.vue';

export default {
  name: 'FilterEstimations',
  mixins: [
    isComponentLoading,
    regionsFilter,
    payedFilter,
    searchTextFilter,
    searchCleanerFilter,
    estimationStatusFilter,
    estimationTypeFilter,
  ],
  components: {
    DownloadFileButton,
  },
  props: {
    defaultSearchParams: {
      type: Object,
      default: () => ({
        search_contact: '',
        search_cleaner: '',
      }),
    },
  },
  data() {
    return {
      MANAGER_TOP,
      useNullableEstimationStatus: false,
      useNullableEstimationType: true,
      searchParams: {},
      menuFrom: false,
      menuTo: false,
    };
  },
  mounted() {
    this.searchParams = {
      search_contact: this.defaultSearchParams.search_contact,
      search_cleaner: this.defaultSearchParams.search_cleaner,
      from: moment()
        .subtract(7, 'days')
        .startOf('day')
        .format(DATE_ISO),
      to: null,
      status: null,
      statuses: [
        ORDERED,
        WAITING_PHOTO,
        ESTIMATING,
        ESTIMATED,
        NOT_SUITABLE,
      ],
      type: null,
      is_payed: null,
      region: null,
    };
  },
  computed: {
    account() {
      return this.$store.getters['account/account'];
    },
    fromDateFormatted() {
      return this.formatDate(this.searchParams.from);
    },
    toDateFormatted() {
      return this.formatDate(this.searchParams.to);
    },
  },
  watch: {
    searchParams: {
      handler(newVal) {
        const data = Object.assign({}, newVal);
        // data.statuses = [];
        data.types = [];
        /* if (data.status) {
          data.statuses = [data.status];
        } */
        if (data.type) {
          data.types = [data.type];
        }
        this.$emit('filtering', data);
      },
      deep: true,
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },
    changeDate(type, date) {
      if (date === null) {
        this.searchParams[type] = null;
      }
    },
    getCurrentLocale() {
      return window.localStorage.language;
    }
  },
};
</script>

<style scoped>

</style>
